((scope) => {
  "use strict";

  const BATCH_STATUS_LABELS = Object.freeze({
    OPEN: "Building this week’s pull batch",
    READY: "Ready to choose fulfillment",
    DRAFT_SYNCING: "Updating weekly pulls",
    DRAFT_MUTATING: "Weekly pulls need review",
    DRAFT_RECOVERING: "Weekly pulls need review",
    DRAFT_RECOVERY_FAILED: "Weekly pulls need review",
    FULFILLMENT_UPDATING: "Updating fulfillment",
    FULFILLMENT_SELECTED: "Fulfillment saved",
    PAYMENT_STARTED: "Checkout started",
    PARTIALLY_PAID: "Partially paid",
    PAID: "Paid",
    COMPLETED: "Completed",
    CANCELED: "Canceled",
  });
  const ITEM_STATUSES = new Set(["PENDING", "CONTACTED", "HELD", "EXTENDED", "PULLED", "RELEASED", "COMPLETED"]);
  const SELECTABLE_FULFILLMENT_STATUSES = new Set(["READY", "FULFILLMENT_SELECTED"]);
  const PAYABLE_STATUSES = new Set(["FULFILLMENT_SELECTED", "PAYMENT_STARTED"]);
  const FULFILLMENT_REQUIRED_STATUSES = new Set(["FULFILLMENT_SELECTED", "PAYMENT_STARTED", "PARTIALLY_PAID", "PAID", "COMPLETED"]);
  const FULFILLMENT_FORBIDDEN_STATUSES = new Set(["OPEN", "READY"]);

  function isRecord(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
  }

  function isCents(value) {
    return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
  }

  function isNullableCents(value) {
    return value === null || isCents(value);
  }

  function isNonEmptyText(value) {
    return typeof value === "string" && Boolean(value.trim());
  }

  function isWeekKey(value) {
    if (typeof value !== "string") return false;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  }

  function isIsoTimestamp(value) {
    if (typeof value !== "string" || !value.trim()) return false;
    const date = new Date(value);
    return !Number.isNaN(date.getTime()) && date.toISOString() === value;
  }

  function isValidWeeklyPullBatch(batch) {
    if (!isRecord(batch) || !isNonEmptyText(batch.id)) return false;
    if (!Number.isSafeInteger(batch.lifecycleVersion) || batch.lifecycleVersion < 0) return false;
    if (!isWeekKey(batch.weekKey) || !isNonEmptyText(batch.weekLabel) || !isIsoTimestamp(batch.updatedAt)) return false;

    const status = String(batch.status || "");
    if (!Object.prototype.hasOwnProperty.call(BATCH_STATUS_LABELS, status) || batch.statusLabel !== BATCH_STATUS_LABELS[status]) return false;
    if (typeof batch.fulfillmentLocked !== "boolean") return false;
    if (batch.fulfillmentLocked !== !SELECTABLE_FULFILLMENT_STATUSES.has(status)) return false;
    if (!Object.prototype.hasOwnProperty.call(batch, "fulfillmentMethod")) return false;
    const fulfillmentMethod = batch.fulfillmentMethod;
    if (!["PICKUP", "SHIPPING", null].includes(fulfillmentMethod)) return false;
    if (FULFILLMENT_REQUIRED_STATUSES.has(status) && fulfillmentMethod == null) return false;
    if (FULFILLMENT_FORBIDDEN_STATUSES.has(status) && fulfillmentMethod != null) return false;

    if (!Array.isArray(batch.items) || !isRecord(batch.money) || !isRecord(batch.fulfillmentOptions)) return false;
    if (!isRecord(batch.fulfillmentOptions.pickup) || !isRecord(batch.fulfillmentOptions.shipping)) return false;
    if (typeof batch.itemsTruncated !== "boolean" || typeof batch.payable !== "boolean") return false;
    if (!Number.isSafeInteger(batch.displayedItemCount) || !Number.isSafeInteger(batch.totalItemCount)) return false;
    if (batch.displayedItemCount < 0 || batch.totalItemCount < batch.displayedItemCount || batch.items.length !== batch.displayedItemCount) return false;
    if (batch.itemsTruncated !== (batch.totalItemCount > batch.displayedItemCount)) return false;

    const pickup = batch.fulfillmentOptions.pickup;
    if (pickup.method !== "PICKUP" || !isNonEmptyText(pickup.label) || pickup.priceCents !== 0) return false;
    const shipping = batch.fulfillmentOptions.shipping;
    if (shipping.method !== "SHIPPING" || !isNonEmptyText(shipping.label) || typeof shipping.available !== "boolean") return false;
    if (!isNullableCents(shipping.priceCents)) return false;
    if (shipping.available !== (isCents(shipping.priceCents) && shipping.priceCents > 0)) return false;

    const itemsValid = batch.items.every((item) => {
      if (!isRecord(item) || !isNonEmptyText(item.title)) return false;
      if (!Number.isSafeInteger(item.quantity) || item.quantity <= 0 || !ITEM_STATUSES.has(String(item.status || ""))) return false;
      if (!isNullableCents(item.unitPriceCents) || !isNullableCents(item.lineTotalCents)) return false;
      if ((item.unitPriceCents === null) !== (item.lineTotalCents === null)) return false;
      return item.unitPriceCents === null || item.lineTotalCents === item.unitPriceCents * item.quantity;
    });
    if (!itemsValid) return false;

    const money = batch.money;
    if (!/^[A-Z]{3}$/.test(String(money.currencyCode || ""))) return false;
    if (![money.subtotalCents, money.discountCents, money.shippingCents, money.taxCents, money.totalCents].every(isNullableCents)) return false;
    if (!["PENDING", "INCOMPLETE", "COMPLETE"].includes(money.calculationState)) return false;

    const completeMoney = [money.subtotalCents, money.discountCents, money.shippingCents, money.taxCents, money.totalCents].every(isCents);
    if (money.calculationState === "COMPLETE") {
      if (!completeMoney || !["PICKUP", "SHIPPING"].includes(batch.fulfillmentMethod)) return false;
      if (money.discountCents > money.subtotalCents) return false;
      if (batch.fulfillmentMethod === "PICKUP" && money.shippingCents !== 0) return false;
      if (batch.fulfillmentMethod === "SHIPPING" && (!shipping.available || money.shippingCents !== shipping.priceCents)) return false;
      const expectedTotal = Math.max(0, money.subtotalCents - money.discountCents) + money.shippingCents + money.taxCents;
      if (!Number.isSafeInteger(expectedTotal) || money.totalCents !== expectedTotal) return false;
    } else if (batch.payable) {
      return false;
    }

    if (batch.fulfillmentMethod === "SHIPPING" && (!shipping.available || !isCents(money.shippingCents) || money.shippingCents <= 0)) return false;
    if (batch.payable) {
      if (!PAYABLE_STATUSES.has(status)) return false;
      const completeItems = !batch.itemsTruncated
        && batch.totalItemCount > 0
        && batch.displayedItemCount === batch.totalItemCount
        && batch.items.every((item) => isCents(item.unitPriceCents) && isCents(item.lineTotalCents));
      if (money.calculationState !== "COMPLETE" || !completeItems || !["PICKUP", "SHIPPING"].includes(batch.fulfillmentMethod)) return false;
    }
    return true;
  }

  function normalizePayload(data) {
    if (!isRecord(data)) throw new Error("Your account returned incomplete data. Please try again.");
    const version = Number(data.version);
    if (version === 2) return { ...data, weeklyPulls: [], weeklyPullTotalCount: 0 };
    if (version !== 3 || !Array.isArray(data.weeklyPulls) || !data.weeklyPulls.every(isValidWeeklyPullBatch)) {
      throw new Error("Weekly pull details are incomplete. Reload your account before taking action.");
    }
    if (!Number.isSafeInteger(data.weeklyPullTotalCount) || data.weeklyPullTotalCount < data.weeklyPulls.length) {
      throw new Error("Weekly pull history is incomplete. Reload your account before taking action.");
    }
    return data;
  }

  scope.DarkstoneWeeklyPullContract = Object.freeze({ isCents, isValidWeeklyPullBatch, normalizePayload });
})(globalThis);
